/**
 * CSRF Protection Utility
 *
 * Implements the double-submit cookie pattern for CSRF protection.
 * The server sets a CSRF token in a readable cookie, and the client
 * sends it back in a header. The server validates that both match.
 *
 * SECURITY: This works because:
 * 1. Attackers can't read cookies from other domains (same-origin policy)
 * 2. Attackers can't set custom headers in cross-origin requests
 * 3. The server verifies the header matches the cookie
 */

import { CSRF_COOKIE_NAME, CSRF_HEADER_NAME } from "./constants";

/** Get CSRF token from cookie. Returns null if not found. */
export function getCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === CSRF_COOKIE_NAME && value) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/** Get CSRF headers for fetch requests. Returns empty object if no token. */
export function getCsrfHeaders(): Record<string, string> {
  const token = getCsrfToken();
  if (token) {
    return { [CSRF_HEADER_NAME]: token };
  }
  return {};
}

/** Check if CSRF token is available */
export function hasCsrfToken(): boolean {
  return getCsrfToken() !== null;
}
